<script lang="ts">
import { stripSiteDomain } from '$lib/utils/url.js';
import { EFourColListDesktopLayout, ETitleFontSizes } from '../../../config/enums.js';
import Link from '../../atoms/Link/Link.svelte';
import BlockHeader from '../BlockHeader/BlockHeader.svelte';

interface Props {
    list?: {
        title?: string;
        listItem: string;
        linkTo?: string;
        linkText?: string;
    }[];
    header?: string;
    headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
    titleSize?: string;
    ctaTo?: string;
    ctaText?: string;
    desktopLayout?: string;
    hideBlock?: boolean;
}

let {
    list = [],
    header = '',
    headingLevel = 'h2',
    titleSize = ETitleFontSizes.H2,
    ctaTo = '',
    ctaText = '',
    desktopLayout = EFourColListDesktopLayout.FOUR_COLUMNS,
    hideBlock = false,
}: Props = $props();

// Transform URLs in list array from Builder.io
const transformedList = $derived(
    list.map((item) => ({
        ...item,
        linkTo: stripSiteDomain(item.linkTo),
    })),
);

const desktopLayoutOptions = Object.values(EFourColListDesktopLayout);
const resolvedDesktopLayout = $derived(
    desktopLayoutOptions.includes(desktopLayout)
        ? desktopLayout
        : EFourColListDesktopLayout.FOUR_COLUMNS,
);
const isTwoByTwoDesktop = $derived(resolvedDesktopLayout === EFourColListDesktopLayout.TWO_BY_TWO);
</script>

{#if !hideBlock}
    <div class="four-col-lists" class:four-col-lists--desktop-two-by-two={isTwoByTwoDesktop}>
        <div class="four-col-lists__header">
            <BlockHeader {header} {headingLevel} {titleSize} {ctaTo} {ctaText} />
        </div>
        <div class="four-col-lists__grid">
            {#each transformedList as colItem, index (index)}
                <div class="four-col-lists__grid-item" data-aos="fade-up">
                    <p>{colItem.title}</p>
                    <ul>
                        {#each colItem.listItem.split('\n') as bullet, index (index)}
                            <li>{bullet}</li>
                        {/each}
                    </ul>
                    {#if colItem.linkTo && colItem.linkText}
                        <Link to={colItem.linkTo} type="link" class="link-text four-col-lists__link">
                            {#snippet text()}{colItem.linkText}{/snippet}
                        </Link>
                    {/if}
                </div>
            {/each}
        </div>
    </div>
{/if}

<style lang="scss">
    @use '/src/scss/global-constants/fonts';
    @use '/src/scss/global-constants/mq-config';
    @use 'sass-mq/mq' as *;

    .four-col-lists {
        --four-col-lists-content-max-width: 112rem;
        --four-col-lists-page-margin: var(--layout-pagegrid-margin, clamp(1.6rem, 5vw, 6.4rem));

        padding-left: var(--four-col-lists-page-margin);
        padding-right: var(--four-col-lists-page-margin);
    }

    .four-col-lists__header {
        @include mq($from: desktop) {
            margin-left: auto;
            margin-right: auto;
            max-width: var(--four-col-lists-content-max-width);
        }
    }

    .four-col-lists__grid {
        column-gap: var(--four-col-lists-page-margin);
        display: grid;
        grid-template-columns: 1fr;
        row-gap: 2.4rem;

        @include mq($from: tablet) {
            grid-template-columns: repeat(2, 1fr);
        }

        @include mq($from: desktop) {
            grid-template-columns: repeat(4, 1fr);
            margin-left: auto;
            margin-right: auto;
            max-width: var(--four-col-lists-content-max-width);
            row-gap: var(--four-col-lists-page-margin);
        }
    }

    .four-col-lists--desktop-two-by-two {
        --four-col-lists-content-max-width: 72rem;
    }

    .four-col-lists--desktop-two-by-two .four-col-lists__grid {
        @include mq($from: desktop) {
            grid-template-columns: repeat(2, minmax(0, 1fr));
        }
    }

    .four-col-lists :global(.four-col-lists__link) {
        color: #111;
        font-size: 1.2rem;
        line-height: 1.5;
        text-transform: uppercase;

        &:hover {
            text-decoration: none;
        }
    }

    p {
        font-family: fonts.$fonts-secondary;
        font-size: 1.4rem;
        font-weight: fonts.$fonts-weight--medium;
        line-height: 2.2rem;
        margin-bottom: 1rem;

        @include mq($from: tablet) {
            font-size: 1.6rem;
            line-height: 2.6rem;
        }
    }

    ul {
        color: var(--colour-squant);
        font-family: fonts.$fonts-secondary;
        font-size: 1.4rem;
        font-weight: fonts.$fonts-weight--normal;
        line-height: 2.2rem;
        margin: 0 0 1rem 0;
        padding: 0;

        @include mq($from: tablet) {
            font-size: 1.6rem;
            line-height: 2.6rem;
        }
    }

    li {
        line-height: 1.3;
        list-style-type: none;
        font-size: 1.4rem;

        @include mq($from: tablet) {
            font-size: 1.6rem;
        }
    }
</style>
